import type { CqDeviceAp } from '../api/connectionQuality'

/**
 * "Access points" pe ecranul Connection Quality: AP-urile fizice la care a
 * stat aparatul in perioada afisata (respecta zoom-ul), cel mai mult stat
 * intai - cat timp, semnalul median / minim, retransmisiile mediane, marcajele
 * Weak AP / Sticky si etajul pe care e pus AP-ul. Totul vine judecat de pe
 * server (DeviceAps.php). Fara AP-uri (fara date Wi-Fi, ori fara BSSID) nu
 * se deseneaza nimic.
 */

function dbm(n: number | null): string {
  return n === null ? '-' : `${Math.round(n)} dBm`
}

export function minutesLabel(m: number): string {
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h < 24) return r ? `${h} h ${r} min` : `${h} h`
  const d = Math.floor(h / 24)
  return `${d} d ${h % 24} h`
}

export function CqAccessPoints({ aps }: { aps: CqDeviceAp[] }) {
  if (aps.length === 0) return null
  return (
    <section className="cq-aps" data-section="access-points">
      <h5>
        <i className="fas fa-broadcast-tower" aria-hidden="true"></i> Access points
        <span className="cq-muted cq-aps-sub"> in this period</span>
      </h5>
      <div className="table-responsive">
        <table className="table table-condensed cq-aps-table">
          <thead>
            <tr>
              <th>Access point</th>
              <th>Office</th>
              <th>Time on it</th>
              <th>Signal (median / min)</th>
              <th>Retries (median)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {aps.map((ap) => (
              <tr key={ap.key} className={ap.current ? 'is-current' : ''} data-ap={ap.prefix} data-current={ap.current ? 'yes' : 'no'}>
                <td>
                  <strong>{ap.label}</strong>
                  {ap.current ? <span className="label label-primary cq-aps-now">connected now</span> : null}
                  {ap.name ? <div className="cq-muted cq-aps-bssids">{ap.bssids.join(', ')}</div> : ap.bssids.length > 1 ? (
                    <div className="cq-muted cq-aps-bssids">{ap.bssids.slice(1).join(', ')}</div>
                  ) : null}
                </td>
                <td>{ap.office ?? <span className="cq-muted">-</span>}</td>
                <td>
                  <div className="cq-aps-bar" aria-hidden="true">
                    <span style={{ width: `${Math.max(1, Math.min(100, ap.pct))}%` }}></span>
                  </div>
                  {ap.pct}% <span className="cq-muted">· {minutesLabel(ap.minutes)}</span>
                </td>
                <td>
                  {dbm(ap.rssi_median)} <span className="cq-muted">/ {dbm(ap.rssi_min)}</span>
                </td>
                <td>{ap.retry_median === null ? '-' : `${ap.retry_median}%`}</td>
                <td className="cq-aps-flags">
                  {ap.weak ? (
                    <span className="label label-warning" data-flag="weak" title="Low median signal or high retries on this AP for this device">
                      Weak AP
                    </span>
                  ) : null}
                  {ap.sticky ? (
                    <span className="label label-danger" data-flag="sticky" title="The device stayed on this AP with a weak signal instead of moving to a closer one">
                      Sticky
                    </span>
                  ) : null}
                  {ap.floor ? (
                    <a className="cq-aps-floor" href={`/app/settings/floor_plans/${ap.floor.id}`} target="_blank" rel="noopener">
                      on {ap.floor.name}
                    </a>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
